
import React, { useEffect } from 'react';
import CircularProgress from '../CircularProgress';
import TimerDisplay from '../TimerDisplay';
import TimerPlaybackControls from './TimerPlaybackControls';
import { TimerType } from '@/hooks/timer/useTimer';

interface TimerClockProps {
  progress: number;
  minutes: number;
  seconds: number;
  isRunning: boolean;
  timerTitle: string;
  isMobile: boolean;
  timerSize: number;
  onTogglePlay: () => void;
  onPrevious: () => void;
  onNext: () => void;
  timerType: TimerType;
  onChangeTimerType: (type: TimerType) => void;
}

// Memoize TimerClock for performance
const TimerClock: React.FC<TimerClockProps> = React.memo(({
  progress,
  minutes,
  seconds,
  isRunning,
  timerTitle,
  isMobile,
  timerSize,
  onTogglePlay,
  onPrevious,
  onNext,
  timerType,
  onChangeTimerType
}) => {
  console.log("TimerClock rendering", { minutes, seconds, isRunning, timerTitle, timerType });
  
  // Add debugging effect to monitor props changes
  useEffect(() => {
    console.log("TimerClock - Props updated", { minutes, seconds, isRunning });
  }, [minutes, seconds, isRunning]);

  // Reverted to original timer size logic
  const containerStyle = isMobile ? {
    width: `${Math.min(timerSize, window.innerWidth - 50)}px`,
    height: `${Math.min(timerSize, window.innerWidth - 50)}px`
  } : {
    width: `${timerSize}px`,
    height: `${timerSize}px`
  };

  const actualSize = isMobile ? Math.min(timerSize, window.innerWidth - 50) : timerSize;

  return (
    <div className="relative flex items-center justify-center" style={containerStyle}>
      {/* Circular Background */}
      <div 
        className="absolute rounded-full bg-black/30 backdrop-blur-sm" 
        style={{
          width: `${actualSize}px`,
          height: `${actualSize}px`,
        }}
      ></div>
      
      <div className="absolute inset-0 flex items-center justify-center">
        <CircularProgress progress={progress} size={actualSize} strokeWidth={isMobile ? 10 : 15} />
      </div>
      
      <div className="flex flex-col items-center justify-center text-center z-10" style={{
        width: '80%', 
        maxWidth: '400px',
        textShadow: "0 1px 3px rgba(0, 0, 0, 0.7)" 
      }}>
        <div className="text-white text-xl font-light mb-2">
          {timerTitle}
        </div>
        
        {/* Timer display with tighter spacing */}
        <div className="mb-3">
          <TimerDisplay minutes={minutes} seconds={seconds} isMobile={isMobile} />
        </div>
        
        {/* Play/Pause, Previous, and Next controls moved closer */}
        <div>
          <TimerPlaybackControls 
            isRunning={isRunning}
            onTogglePlay={onTogglePlay}
            onPrevious={onPrevious}
            onNext={onNext}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
});

TimerClock.displayName = 'TimerClock';

export default TimerClock;
